import React from "react";
import { QUIZZES_BY_ID } from "../../data/quizzes";

const DIMENSION_ICONS = Object.fromEntries(
  Object.entries(QUIZZES_BY_ID).map(([id, q]) => [id, q.emoji])
);

export default function ProfileAnalysisView({ pro, jobTitle, onBack, onAskQuestion, synthesis, isLoading, hasError, onRetry }) {

  // Loader pendant la génération
  if (isLoading) {
    return (
      <div className="flex flex-1 flex-col h-full gap-4 px-4 md:py-8 pt-14 overflow-y-auto w-full mx-auto max-w-lg overflow-x-hidden no-scrollbar">
        {/* Header avec retour */}
        <div className="mb-2">
          <button
            onClick={onBack}
            className="text-gray-400 hover:text-white flex items-center gap-2 text-sm"
          >
            ← Tableau de bord
          </button>
        </div>

        {/* Loader animé */}
        <div className="flex-1 flex flex-col items-center justify-center gap-6 py-12">
          {/* Animation de cercles */}
          <div className="relative w-20 h-20">
            <div className="absolute inset-0 rounded-full border-4 border-slate-700"></div>
            <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-emerald-500 animate-spin"></div>
            <div className="absolute inset-2 rounded-full border-4 border-transparent border-t-amber-500 animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}></div>
          </div>

          {/* Texte */}
          <div className="text-center">
            <h2 className="text-white text-lg font-semibold mb-2">
              Analyse en cours...
            </h2>
            <p className="text-gray-400 text-sm max-w-xs">
              On compare ton profil avec ce métier pour te donner une analyse personnalisée
            </p>
          </div>

          {/* Indicateurs de progression */}
          <div className="flex gap-3 mt-4">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-800 rounded-full">
              <span>💎</span>
              <span className="text-xs text-gray-400">Valeurs</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-800 rounded-full">
              <span>🧭</span>
              <span className="text-xs text-gray-400">Intérêts</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-800 rounded-full">
              <span>⚡</span>
              <span className="text-xs text-gray-400">Énergies</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-800 rounded-full">
              <span>🧠</span>
              <span className="text-xs text-gray-400">Intelligences</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // État d'erreur avec bouton retry
  if (hasError) {
    return (
      <div className="flex flex-1 flex-col h-full gap-4 px-4 md:py-8 pt-14 overflow-y-auto w-full mx-auto max-w-lg overflow-x-hidden no-scrollbar">
        {/* Header avec retour */}
        <div className="mb-2">
          <button
            onClick={onBack}
            className="text-gray-400 hover:text-white flex items-center gap-2 text-sm"
          >
            ← Tableau de bord
          </button>
        </div>

        {/* État d'erreur */}
        <div className="flex-1 flex flex-col items-center justify-center gap-6 py-12">
          {/* Icône d'erreur */}
          <div className="w-20 h-20 rounded-full bg-red-500/10 flex items-center justify-center">
            <span className="text-4xl">⚠️</span>
          </div>

          {/* Texte */}
          <div className="text-center">
            <h2 className="text-white text-lg font-semibold mb-2">
              Oups, une erreur est survenue
            </h2>
            <p className="text-gray-400 text-sm max-w-xs">
              Impossible de générer ton analyse personnalisée. Réessaie dans quelques instants.
            </p>
          </div>

          {/* Bouton retry */}
          <button
            onClick={onRetry}
            className="px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded-xl transition-colors flex items-center gap-2"
          >
            <span>🔄</span>
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  // Questions pour le pro basées sur la synthèse
  const questionsForPro = synthesis?.questions || [];

  return (
    <div className="flex flex-1 flex-col gap-4 px-4 md:py-8 pt-14 w-full bg-gray-900">
      <div className="mx-auto max-w-lg ">
        {/* Header avec retour */}
        <div className="mb-2">
          <button
            onClick={onBack}
            className="text-gray-400 hover:text-white flex items-center gap-2 text-sm"
          >
            ← Tableau de bord
          </button>
        </div>

        {/* Titre principal */}
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-full bg-slate-600 flex items-center justify-center overflow-hidden">
            {pro?.photoUrl ? (
              <img src={pro.photoUrl} alt={pro.name} className="w-full h-full object-cover" />
            ) : (
              <svg className="w-8 h-8 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
              </svg>
            )}
          </div>
          <div>
            <h1 className="text-white text-xl font-bold">Ton profil complet & ce métier</h1>
            <p className="text-gray-400 text-sm">{jobTitle} • {pro?.name}</p>
          </div>
        </div>

        {/* Badge Premium */}
        <div className="mb-4 inline-flex items-center gap-2 px-3 py-1.5 bg-amber-500/10 border border-amber-500/30 rounded-full w-fit">
          <span className="text-amber-400">⭐</span>
          <span className="text-amber-400 text-sm font-medium">Analyse Premium</span>
        </div>

        {/* Blocs d'analyse */}
        <div className="space-y-4">
          {/* Ce métier & toi — Synthèse */}
          <div className="bg-slate-800 rounded-xl p-5 border border-slate-700">
            <h2 className="text-white font-semibold mb-4">Ce métier & toi</h2>
            <p className="text-gray-300 text-sm leading-relaxed">
              {synthesis?.ce_metier_et_toi || "Analyse en cours..."}
            </p>
          </div>

          {/* Là où ça pourrait résonner */}
          <div className="bg-slate-800 rounded-xl p-5 border border-slate-700">
            <h2 className="text-emerald-400 font-semibold mb-3">Là où ça pourrait résonner</h2>
            <p className="text-gray-300 text-sm leading-relaxed">
              {synthesis?.la_ou_ca_resonne || "Analyse en cours..."}
            </p>
          </div>

          {/* Ce qui pourrait te surprendre */}
          <div className="bg-slate-800 rounded-xl p-5 border border-slate-700">
            <h2 className="text-amber-400 font-semibold mb-3">Ce qui pourrait te surprendre</h2>
            <p className="text-gray-300 text-sm leading-relaxed">
              {synthesis?.ce_qui_pourrait_te_surprendre || "Analyse en cours..."}
            </p>
          </div>
    
          {/* Là où ça pourrait résonner */}
          <div className="bg-slate-800 rounded-xl p-5 border border-slate-700">
            <h2 className="text-emerald-400 font-semibold mb-3">
              <span className="text-lg">📍</span> Où ce métier pourrait te plaire
            </h2>
            <p className="text-gray-300 text-sm leading-relaxed">
              {synthesis?.ou_ce_metier_pourrait_te_plaire || "Analyse en cours..."}
            </p>
          </div>

          {/* Questions à creuser */}
          <div className="bg-slate-800 rounded-xl p-5 border border-slate-700">
            <h2 className="text-blue-400 font-semibold mb-3">Questions à te poser</h2>
            <p className="text-gray-300 text-sm leading-relaxed">
              {synthesis?.questions_a_te_poser || "Analyse en cours..."}
            </p>
          </div>

          {/* Demande l'avis du pro */}
          {questionsForPro.length > 0 && (
            <div className="bg-slate-800 rounded-xl p-5 border border-slate-700">
              <h2 className="text-white font-semibold mb-4 flex items-center gap-2">
                <span className="text-lg">💬</span> Demande l'avis de {pro?.name}
              </h2>

              <div className="space-y-2">
                {questionsForPro.map((q, index) => (
                  <button
                    key={index}
                    onClick={() => onAskQuestion(q.question)}
                    className="w-full text-left p-3 bg-slate-700/50 hover:bg-slate-700 rounded-lg border border-slate-600 hover:border-emerald-500/50 transition-all group"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xl">{DIMENSION_ICONS[q.dimension] || '💬'}</span>
                      <div className="flex-1">
                        <p className="text-gray-200 text-sm group-hover:text-white transition-colors">
                          "{q.question}"
                        </p>
                        <p className="text-gray-500 text-xs mt-0.5">
                          Basé sur {q.element_profile}
                        </p>
                      </div>
                      <span className="text-slate-600 group-hover:text-emerald-400 transition-colors">→</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Message de confirmation */}
        <div className="mt-2 mb-6 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-center">
          <p className="text-emerald-400 text-sm">
            ✓ Analyse basée sur tes 4 questionnaires complétés
          </p>
        </div>
      </div>
    </div>
  );
}
